import React from 'react';
import { definePage, defineResource, ListQuery } from '@restavo/crud';
import EmptyState from 'src/components/Base/EmptyState';
import MessageDetailForm from 'src/components/Storefront/MessageDetailForm';
import { useTenantContext } from 'src/context/useTenantContext';
import {
  tenantStorefrontMessages,
  tenantStorefrontMessageUpdate,
  RestaurantScope,
} from 'src/services/apis/tenantRequests';
import { StorefrontContactMessageEntity } from 'src/services/apis/types';
import { QUERIES } from 'src/utils/react-query';

/**
 * Mirrors `StorefrontMessageStatusEnum` in `apps/server/src/storefront/storefront.enum.ts`.
 *
 * A copy, not an import: the portal cannot reach into the server's source, and the generated
 * client types are no substitute — Swagger publishes this enum as the string union
 * `'NEW' | 'HANDLED' | 'SPAM'` while the column is numeric and the API stores/expects
 * `1`/`2`/`3`. Sending the string makes the server's `@IsIn([1, 2, 3])` validation reject the
 * write; reading it back gives a number. The same duplication the Zalo client documents for
 * order statuses. Adding a member here is an append on both sides, never a migration.
 */
export const STOREFRONT_MESSAGE_STATUS = { NEW: 1, HANDLED: 2, SPAM: 3 } as const;

/**
 * The `status` cell's tag map, keyed by the numeric value the API returns.
 */
export const STOREFRONT_MESSAGE_STATUS_TAG_MAP: Record<string, { labelKey: string; type: string }> = {
  [STOREFRONT_MESSAGE_STATUS.NEW]: { labelKey: 'storefrontMessages.statusNew', type: 'blue' },
  [STOREFRONT_MESSAGE_STATUS.HANDLED]: { labelKey: 'storefrontMessages.statusHandled', type: 'green' },
  [STOREFRONT_MESSAGE_STATUS.SPAM]: { labelKey: 'storefrontMessages.statusSpam', type: 'red' },
};

/**
 * The first line of a message, clipped. The full text belongs in the detail modal — a table
 * cell that rendered a 2000-character message would make the list unreadable.
 */
const firstLine = (message: string): string => {
  const line = message.split('\n')[0] ?? '';
  return line.length > 80 ? `${line.slice(0, 80)}…` : line;
};

const storefrontMessages = defineResource({
  key: QUERIES.STOREFRONT_MESSAGES,
  api: {
    list: (scope: RestaurantScope, query?: ListQuery) => tenantStorefrontMessages(scope, query),
    // Unreachable: a message is written by a visitor through the public contact form, never
    // from this screen, and the API registers no create route at all
    // (StorefrontMessagesController `routes.only`). The crud layer still requires all four
    // slots, so these two fail loudly rather than pretending to work.
    create: async () => {
      throw new Error('A contact message is submitted by a visitor; the inbox cannot create one.');
    },
    update: (scope: RestaurantScope, id: string, body: any) => tenantStorefrontMessageUpdate(scope, id, body),
    remove: async () => {
      throw new Error('Contact messages are the record of truth and are never deleted.');
    },
  },
  // The API supports the CRUD `s` param, so search stays server-side: an inbox can outgrow a
  // page, and a client search would silently only ever match the rows already loaded.
  search: 'server',
  searchFields: ['name', 'email'],
  // A status filter is the inbox's only dimension. Its ids are the numeric values the column
  // stores, matching the tag map above.
  filterable: [
    {
      field: 'status',
      labelKey: 'storefrontMessages.status',
      type: 'enum',
      options: [
        { id: String(STOREFRONT_MESSAGE_STATUS.NEW), labelKey: 'storefrontMessages.statusNew' },
        { id: String(STOREFRONT_MESSAGE_STATUS.HANDLED), labelKey: 'storefrontMessages.statusHandled' },
        { id: String(STOREFRONT_MESSAGE_STATUS.SPAM), labelKey: 'storefrontMessages.statusSpam' },
      ],
    },
  ],
  // A message list is a record to read, not a report to take away.
  exportable: false,
});

/**
 * The Messages page config, as a hook to match the pilot's shape. The resource is module-scope
 * because nothing in it is per-tenant: the tenant scope travels as the `scope` argument
 * `useCrudResource` supplies (and is appended to the query key there), so this hook reads the
 * context only to re-render the page when the tenant changes.
 *
 * `capabilities` carries the two write gates that are off for this resource: a message is
 * born from a visitor's submission and may only ever have its status changed.
 */
export const useStorefrontMessagesPage = () => {
  useTenantContext();

  return {
    page: definePage<StorefrontContactMessageEntity, any, any, RestaurantScope, any>(storefrontMessages, {
      titleKey: 'storefrontMessages.title',
      // The storefront layout's h1 already names the page.
      hideTitle: true,
      // Carbon renders this as `<total> <description>` ("12 messages"), so it takes a plural
      // noun rather than the page's sentence-length subtitle.
      descriptionKey: 'storefrontMessages.description',
      // Required by the presentation contract but unreachable: the create button is hidden
      // and no code path can open the form in create mode. The update label is reused so the
      // screen never grows copy that implies a message can be written from here.
      createLabelKey: 'storefrontMessages.update',
      updateLabelKey: 'storefrontMessages.update',
      deleteHeadingKey: 'storefrontMessages.deleteHeading',
      columns: [
        { key: 'name', headerKey: 'storefrontMessages.name', alwaysVisible: true },
        { key: 'email', headerKey: 'storefrontMessages.email' },
        // `date`, not a raw ISO string: columnDisplays.tsx renders it through the adapter's
        // formatter, so the locale and timezone conventions live in one place.
        {
          key: 'createdAt',
          headerKey: 'storefrontMessages.receivedAt',
          sortable: true,
          display: { kind: 'date' },
        },
        // `tag`, not an invented 'enum' kind: columnDisplays.tsx exposes text / currency /
        // number / date / boolean / tag / reference, and the tag map is what carries the
        // numeric status to its three localized labels.
        {
          key: 'status',
          headerKey: 'storefrontMessages.status',
          display: { kind: 'tag', map: STOREFRONT_MESSAGE_STATUS_TAG_MAP },
        },
        // The full text lives in the detail modal; the cell shows only the opening line.
        {
          key: 'message',
          headerKey: 'storefrontMessages.message',
          render: (row: StorefrontContactMessageEntity) => firstLine(row.message ?? ''),
        },
      ],
      form: MessageDetailForm,
      // Only the status is writable, so the payload carries only the update DTO's two fields.
      // An empty `handledAt` must become null: the server validates it as a date string and
      // rejects ''. The API's whitelist strips the `id` the crud layer adds alongside these.
      toPayload: (values: any) => ({
        status: Number(values.status),
        handledAt: values.handledAt || null,
      }),
      emptyState: <EmptyState />,
    }),
    capabilities: { canCreate: false, canDelete: false },
  };
};
